/**
 * API Keys Page
 *
 * Page for managing API keys with full CRUD functionality.
 * Features:
 * - List of API keys (table showing name, prefix, scopes, status, last used, created)
 * - Generate API Key button that opens a modal form
 * - Generate form: name input, scopes multi-select, optional expiry date
 * - After generation: shows the full API key in a copyable alert
 * - Revoke button per key (with confirmation)
 * - Update button to edit name/scopes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container } from '../components/layout';
import { apiKeysService } from '../services/apiKeysService';
import { logger } from '../services/loggerService';
import { useAuth } from '../contexts/AuthContext';
import { Toast } from '../components/feedback';
import { Button, Badge } from '../components/ui';
import type { ApiKey, ApiKeyCreateResponse, AvailableScope } from '../services/apiKeysService';
import type { ToastType } from '../components/feedback';

/**
 * Toast state interface
 */
interface ToastState {
  message: string;
  type: ToastType;
}

/**
 * Format date as DD/MM/YYYY with leading zeros
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * ApiKeysPage Component
 *
 * Manage API keys: create, list, update, and revoke.
 */
export const ApiKeysPage: React.FC = () => {
  // Data state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [availableScopes, setAvailableScopes] = useState<AvailableScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);

  // Generated key display
  const [generatedKey, setGeneratedKey] = useState<ApiKeyCreateResponse | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  // Form state for create
  const [createName, setCreateName] = useState('');
  const [createScopes, setCreateScopes] = useState<string[]>([]);
  const [createExpiresAt, setCreateExpiresAt] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Form state for edit
  const [editName, setEditName] = useState('');
  const [editScopes, setEditScopes] = useState<string[]>([]);
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Revoke state
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);

  // Toast state
  const [toast, setToast] = useState<ToastState | null>(null);

  // Permission checking
  const { hasPermission } = useAuth();

  // CSS variable styles for theming
  const cardStyle = { backgroundColor: 'var(--color-background-card, #ffffff)' };
  const textPrimaryStyle = { color: 'var(--color-text-primary, #111827)' };
  const textSecondaryStyle = { color: 'var(--color-text-secondary, #6b7280)' };
  const borderStyle = { borderColor: 'var(--color-border-default, #e5e7eb)' };

  /**
   * Fetch API keys from the backend
   */
  const fetchApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiKeysService.getApiKeys();
      setApiKeys(data);
      setError(null);
    } catch (err) {
      logger.error('Failed to fetch API keys', err);
      setError('Failed to load API keys. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch available scopes
   */
  const fetchScopes = useCallback(async () => {
    try {
      const data = await apiKeysService.getAvailableScopes();
      setAvailableScopes(data);
    } catch (err) {
      logger.error('Failed to fetch available scopes', err);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchApiKeys();
    fetchScopes();
  }, [fetchApiKeys, fetchScopes]);

  /**
   * Handle opening the create modal
   */
  const handleOpenCreate = () => {
    setCreateName('');
    setCreateScopes([]);
    setCreateExpiresAt('');
    setGeneratedKey(null);
    setKeyCopied(false);
    setShowCreateModal(true);
  };

  /**
   * Handle create API key form submission
   */
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim()) return;

    setCreateSubmitting(true);
    try {
      const result = await apiKeysService.createApiKey({
        name: createName.trim(),
        scopes: createScopes,
        expires_at: createExpiresAt || undefined,
      });
      setGeneratedKey(result);
      setKeyCopied(false);
      fetchApiKeys();
      setToast({ message: `API key "${result.name}" created successfully.`, type: 'success' });
    } catch (err) {
      logger.error('Failed to create API key', err);
      setToast({ message: 'Failed to create API key. Please try again.', type: 'error' });
    } finally {
      setCreateSubmitting(false);
    }
  };

  /**
   * Copy generated key to clipboard
   */
  const handleCopyKey = async () => {
    if (generatedKey) {
      try {
        await navigator.clipboard.writeText(generatedKey.key);
        setKeyCopied(true);
        setTimeout(() => setKeyCopied(false), 3000);
      } catch (err) {
        logger.error('Failed to copy key to clipboard', err);
      }
    }
  };

  /**
   * Handle opening the edit modal
   */
  const handleOpenEdit = (key: ApiKey) => {
    setSelectedKey(key);
    setEditName(key.name);
    setEditScopes([...key.scopes]);
    setShowEditModal(true);
  };

  /**
   * Handle edit API key form submission
   */
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedKey || !editName.trim()) return;

    setEditSubmitting(true);
    try {
      const updated = await apiKeysService.updateApiKey(selectedKey.id, {
        name: editName.trim(),
        scopes: editScopes,
      });
      setApiKeys((prev) => prev.map((k) => (k.id === updated.id ? updated : k)));
      setToast({ message: `API key "${updated.name}" updated successfully.`, type: 'success' });
      setShowEditModal(false);
      setSelectedKey(null);
    } catch (err) {
      logger.error('Failed to update API key', err);
      setToast({ message: 'Failed to update API key. Please try again.', type: 'error' });
    } finally {
      setEditSubmitting(false);
    }
  };

  /**
   * Handle opening the revoke confirmation dialog
   */
  const handleOpenRevoke = (key: ApiKey) => {
    setSelectedKey(key);
    setShowRevokeDialog(true);
  };

  /**
   * Handle revoking an API key
   */
  const handleRevoke = async () => {
    if (!selectedKey) return;

    setRevokeSubmitting(true);
    try {
      await apiKeysService.revokeApiKey(selectedKey.id);
      setApiKeys((prev) => prev.filter((k) => k.id !== selectedKey.id));
      setToast({ message: `API key "${selectedKey.name}" revoked successfully.`, type: 'success' });
      setShowRevokeDialog(false);
      setSelectedKey(null);
    } catch (err) {
      logger.error('Failed to revoke API key', err);
      setToast({ message: 'Failed to revoke API key. Please try again.', type: 'error' });
    } finally {
      setRevokeSubmitting(false);
    }
  };

  /**
   * Toggle scope selection
   */
  const toggleScope = (scope: string, currentScopes: string[], setScopes: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (currentScopes.includes(scope)) {
      setScopes(currentScopes.filter((s) => s !== scope));
    } else {
      setScopes([...currentScopes, scope]);
    }
  };

  /**
   * Get status badge variant
   */
  const getKeyStatusVariant = (status: string): 'success' | 'error' | 'warning' | 'neutral' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'revoked':
        return 'error';
      case 'expired':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  /**
   * Close all modals
   */
  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowRevokeDialog(false);
    setSelectedKey(null);
    setGeneratedKey(null);
  };

  return (
    <Container className="py-8" data-testid="api-keys-page">
      {/* Page Header */}
      <div className="page-header page-header--with-actions">
        <div>
          <h1 className="page-title">API Keys</h1>
          <p className="page-subtitle">
            Manage your API keys for programmatic access.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleOpenCreate}
          data-testid="generate-api-key-button"
        >
          Generate API Key
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div
          className="mb-6 p-4 rounded-md border"
          style={{
            backgroundColor: 'var(--color-status-error-light, #fef2f2)',
            borderColor: 'var(--color-error, #ef4444)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--color-error, #ef4444)' }}>{error}</p>
          <button
            type="button"
            className="mt-2 text-sm underline hover:no-underline"
            style={{ color: 'var(--color-error, #ef4444)' }}
            onClick={fetchApiKeys}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="rounded-lg shadow-sm border p-8" style={{ ...cardStyle, ...borderStyle }}>
          <div className="flex items-center justify-center">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--color-primary, #3b82f6)' }}
            />
            <span className="ml-3" style={textSecondaryStyle}>Loading API keys...</span>
          </div>
        </div>
      ) : (
        /* API Keys Table */
        <div className="rounded-lg shadow-sm border overflow-hidden" style={{ ...cardStyle, ...borderStyle }}>
          <table className="min-w-full divide-y" style={{ borderColor: 'var(--color-border-light, #f3f4f6)' }}>
            <thead style={{ backgroundColor: 'var(--color-background-page, #f9fafb)' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                  Prefix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                  Scopes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                  Last Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody style={cardStyle}>
              {apiKeys.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center" style={textSecondaryStyle}>
                    No API keys found. Click "Generate API Key" to create one.
                  </td>
                </tr>
              ) : (
                apiKeys.map((apiKey) => (
                  <tr key={apiKey.id} style={cardStyle} data-testid={`api-key-row-${apiKey.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium" style={textPrimaryStyle}>
                        {apiKey.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code
                        className="text-xs px-2 py-1 rounded"
                        style={{
                          backgroundColor: 'var(--color-background-surface, #f3f4f6)',
                          color: 'var(--color-text-primary, #111827)',
                        }}
                      >
                        {apiKey.prefix}...
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {apiKey.scopes.length > 0 ? (
                          apiKey.scopes.map((scope) => (
                            <Badge key={scope} variant="neutral" size="sm">
                              {scope}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs" style={textSecondaryStyle}>No scopes</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getKeyStatusVariant(apiKey.status)} size="sm">
                        {apiKey.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm" style={textSecondaryStyle}>
                        {apiKey.last_used_at ? formatDate(apiKey.last_used_at) : 'Never'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm" style={textSecondaryStyle}>
                        {formatDate(apiKey.created_at)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {apiKey.status === 'active' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEdit(apiKey)}
                              data-testid={`edit-api-key-${apiKey.id}`}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleOpenRevoke(apiKey)}
                              data-testid={`revoke-api-key-${apiKey.id}`}
                            >
                              Revoke
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleCloseModals}
          />
          {/* Modal */}
          <div
            className="relative z-10 w-full max-w-lg mx-4 rounded-xl shadow-xl p-6 border"
            style={{
              backgroundColor: 'var(--color-background-modal, #ffffff)',
              borderColor: 'var(--color-border-default, #e5e7eb)',
            }}
          >
            {generatedKey ? (
              /* Show Generated Key */
              <div>
                <h2 className="text-lg font-semibold mb-4" style={textPrimaryStyle}>
                  API Key Generated
                </h2>
                <div
                  className="p-4 rounded-lg border mb-4"
                  style={{
                    backgroundColor: 'var(--color-status-error-light, #fef2f2)',
                    borderColor: 'var(--color-warning, #f59e0b)',
                  }}
                >
                  <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-warning, #f59e0b)' }}>
                    Warning: Copy your API key now. It will not be shown again.
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1" style={textSecondaryStyle}>
                    API Key
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={generatedKey.key}
                      className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono"
                      style={{
                        backgroundColor: 'var(--color-background-input, #ffffff)',
                        color: 'var(--color-text-primary, #111827)',
                        borderColor: 'var(--color-border-default, #e5e7eb)',
                      }}
                      data-testid="generated-api-key-value"
                    />
                    <Button
                      variant="primary"
                      onClick={handleCopyKey}
                      data-testid="copy-api-key-button"
                    >
                      {keyCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    onClick={handleCloseModals}
                    data-testid="close-generated-key-button"
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              /* Create Form */
              <form onSubmit={handleCreateSubmit}>
                <h2 className="text-lg font-semibold mb-4" style={textPrimaryStyle}>
                  Generate API Key
                </h2>

                {/* Name Input */}
                <div className="mb-4">
                  <label
                    htmlFor="api-key-name"
                    className="block text-sm font-medium mb-1"
                    style={textSecondaryStyle}
                  >
                    Name
                  </label>
                  <input
                    id="api-key-name"
                    type="text"
                    required
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    placeholder="e.g., Production API Key"
                    className="w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:outline-none"
                    style={{
                      backgroundColor: 'var(--color-background-input, #ffffff)',
                      color: 'var(--color-text-primary, #111827)',
                      borderColor: 'var(--color-border-default, #e5e7eb)',
                    }}
                    data-testid="create-api-key-name"
                  />
                </div>

                {/* Scopes Multi-Select */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={textSecondaryStyle}>
                    Scopes
                  </label>
                  <div
                    className="max-h-48 overflow-y-auto rounded-lg border p-3 space-y-2"
                    style={{
                      backgroundColor: 'var(--color-background-input, #ffffff)',
                      borderColor: 'var(--color-border-default, #e5e7eb)',
                    }}
                  >
                    {availableScopes.length > 0 ? (
                      availableScopes.map((scope) => (
                        <label
                          key={scope.name}
                          className="flex items-start gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={createScopes.includes(scope.name)}
                            onChange={() => toggleScope(scope.name, createScopes, setCreateScopes)}
                            className="mt-0.5 rounded"
                            style={{ accentColor: 'var(--color-primary, #3b82f6)' }}
                          />
                          <div>
                            <span className="text-sm font-medium" style={textPrimaryStyle}>
                              {scope.name}
                            </span>
                            {scope.description && (
                              <p className="text-xs" style={textSecondaryStyle}>
                                {scope.description}
                              </p>
                            )}
                          </div>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm" style={textSecondaryStyle}>
                        No scopes available.
                      </p>
                    )}
                  </div>
                </div>

                {/* Expiry Date */}
                <div className="mb-6">
                  <label
                    htmlFor="api-key-expiry"
                    className="block text-sm font-medium mb-1"
                    style={textSecondaryStyle}
                  >
                    Expiry Date (optional)
                  </label>
                  <input
                    id="api-key-expiry"
                    type="date"
                    value={createExpiresAt}
                    onChange={(e) => setCreateExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:outline-none"
                    style={{
                      backgroundColor: 'var(--color-background-input, #ffffff)',
                      color: 'var(--color-text-primary, #111827)',
                      borderColor: 'var(--color-border-default, #e5e7eb)',
                    }}
                    data-testid="create-api-key-expiry"
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <Button
                    variant="ghost"
                    onClick={handleCloseModals}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={createSubmitting || !createName.trim()}
                    data-testid="submit-create-api-key"
                  >
                    {createSubmitting ? 'Generating...' : 'Generate Key'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Edit API Key Modal */}
      {showEditModal && selectedKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleCloseModals}
          />
          {/* Modal */}
          <div
            className="relative z-10 w-full max-w-lg mx-4 rounded-xl shadow-xl p-6 border"
            style={{
              backgroundColor: 'var(--color-background-modal, #ffffff)',
              borderColor: 'var(--color-border-default, #e5e7eb)',
            }}
          >
            <form onSubmit={handleEditSubmit}>
              <h2 className="text-lg font-semibold mb-4" style={textPrimaryStyle}>
                Edit API Key
              </h2>

              {/* Name Input */}
              <div className="mb-4">
                <label
                  htmlFor="edit-api-key-name"
                  className="block text-sm font-medium mb-1"
                  style={textSecondaryStyle}
                >
                  Name
                </label>
                <input
                  id="edit-api-key-name"
                  type="text"
                  required
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:outline-none"
                  style={{
                    backgroundColor: 'var(--color-background-input, #ffffff)',
                    color: 'var(--color-text-primary, #111827)',
                    borderColor: 'var(--color-border-default, #e5e7eb)',
                  }}
                  data-testid="edit-api-key-name"
                />
              </div>

              {/* Scopes Multi-Select */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={textSecondaryStyle}>
                  Scopes
                </label>
                <div
                  className="max-h-48 overflow-y-auto rounded-lg border p-3 space-y-2"
                  style={{
                    backgroundColor: 'var(--color-background-input, #ffffff)',
                    borderColor: 'var(--color-border-default, #e5e7eb)',
                  }}
                >
                  {availableScopes.length > 0 ? (
                    availableScopes.map((scope) => (
                      <label
                        key={scope.name}
                        className="flex items-start gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={editScopes.includes(scope.name)}
                          onChange={() => toggleScope(scope.name, editScopes, setEditScopes)}
                          className="mt-0.5 rounded"
                          style={{ accentColor: 'var(--color-primary, #3b82f6)' }}
                        />
                        <div>
                          <span className="text-sm font-medium" style={textPrimaryStyle}>
                            {scope.name}
                          </span>
                          {scope.description && (
                            <p className="text-xs" style={textSecondaryStyle}>
                              {scope.description}
                            </p>
                          )}
                        </div>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm" style={textSecondaryStyle}>
                      No scopes available.
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={handleCloseModals}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={editSubmitting || !editName.trim()}
                  data-testid="submit-edit-api-key"
                >
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Revoke Confirmation Dialog */}
      {showRevokeDialog && selectedKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleCloseModals}
          />
          {/* Dialog */}
          <div
            className="relative z-10 w-full max-w-md mx-4 rounded-xl shadow-xl p-6 border"
            style={{
              backgroundColor: 'var(--color-background-modal, #ffffff)',
              borderColor: 'var(--color-border-default, #e5e7eb)',
            }}
          >
            <h2 className="text-lg font-semibold mb-2" style={textPrimaryStyle}>
              Revoke API Key
            </h2>
            <p className="text-sm mb-6" style={textSecondaryStyle}>
              Are you sure you want to revoke the API key{' '}
              <strong style={textPrimaryStyle}>"{selectedKey.name}"</strong>?
              This action cannot be undone. Any applications using this key will
              lose access immediately.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={handleCloseModals}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRevoke}
                disabled={revokeSubmitting}
                data-testid="confirm-revoke-api-key"
              >
                {revokeSubmitting ? 'Revoking...' : 'Revoke Key'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50">
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
            data-testid="api-keys-toast"
          />
        </div>
      )}
    </Container>
  );
};

export default ApiKeysPage;
